import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Building2,
  HardHat,
  Sparkles,
  Trash2,
  Eye,
  CheckCircle2,
  Inbox,
  FileText,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/site/Logo";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  traite: "Traité",
  archive: "Archivé",
};
const STATUS_COLORS = {
  nouveau: "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30",
  en_cours: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  traite: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  archive: "bg-muted text-muted-foreground border-border",
};

const SERVICE_ICONS = {
  bureaux: Building2,
  chantier: HardHat,
  autre: Sparkles,
};
const SERVICE_LABELS = {
  bureaux: "Bureaux",
  chantier: "Chantier",
  autre: "Autre / Mixte",
};

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q, c] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/quote-requests`, { headers: authHeaders() }),
        axios.get(`${API}/admin/contacts`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setQuotes(q.data);
      setContacts(c.data);
    } catch (e) {
      toast.error("Impossible de charger les données.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/quote-requests/${id}`,
        { status },
        { headers: authHeaders() }
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selectedQuote?.id === id) setSelectedQuote({ ...selectedQuote, status });
      toast.success("Statut mis à jour.");
      fetchAll();
    } catch {
      toast.error("Erreur de mise à jour.");
    }
  };

  const deleteQuote = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    try {
      await axios.delete(`${API}/admin/quote-requests/${id}`, { headers: authHeaders() });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setSelectedQuote(null);
      toast.success("Demande supprimée.");
      fetchAll();
    } catch {
      toast.error("Erreur.");
    }
  };

  const markContactRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/read`, {}, { headers: authHeaders() });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, read: true } : c)));
      if (selectedContact?.id === id) setSelectedContact({ ...selectedContact, read: true });
      fetchAll();
    } catch {
      toast.error("Erreur.");
    }
  };

  const deleteContact = async (id) => {
    if (!confirm("Supprimer ce message ?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeaders() });
      setContacts((cs) => cs.filter((c) => c.id !== id));
      setSelectedContact(null);
      toast.success("Message supprimé.");
      fetchAll();
    } catch {
      toast.error("Erreur.");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card/40 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-home-link">
            <Logo size="md" tag="Admin" />
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchAll}
              className="text-sm text-muted-foreground hover:text-[hsl(var(--accent))] flex items-center gap-2"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            <span className="hidden md:inline text-sm text-muted-foreground">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-muted-foreground hover:text-destructive flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="px-6 lg:px-10 pt-10">
        <h1 className="font-serif text-3xl lg:text-4xl mb-2">Tableau de bord</h1>
        <p className="text-muted-foreground text-sm mb-8">Gérez les demandes de devis et messages reçus.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={FileText} label="Demandes de devis" value={stats?.total_quotes ?? 0} accent />
          <StatCard icon={Sparkles} label="Nouvelles" value={stats?.new_quotes ?? 0} />
          <StatCard icon={Inbox} label="Messages" value={stats?.total_contacts ?? 0} accent />
          <StatCard icon={Mail} label="Non lus" value={stats?.unread_contacts ?? 0} />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 lg:px-10 pb-20">
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-card border border-border rounded-sm h-auto p-1">
            <TabsTrigger value="quotes" className="px-6 py-2.5 text-sm" data-testid="tab-quotes">
              Devis ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="px-6 py-2.5 text-sm" data-testid="tab-contacts">
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-8">
            {quotes.length === 0 ? (
              <EmptyState text="Aucune demande de devis pour le moment." />
            ) : (
              <div className="grid gap-3">
                {quotes.map((q) => {
                  const Icon = SERVICE_ICONS[q.service_type] || Sparkles;
                  return (
                    <div
                      key={q.id}
                      className="border border-border rounded-sm p-5 bg-card/40 hover:border-[hsl(var(--accent))]/50 transition-colors"
                      data-testid={`quote-row-${q.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 shrink-0 rounded-full border border-border flex items-center justify-center">
                            <Icon className="w-4 h-4 text-[hsl(var(--accent))]" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium">{q.full_name}</span>
                              {q.company && <span className="text-sm text-muted-foreground">· {q.company}</span>}
                              <Badge className={`text-[10px] uppercase tracking-wider border ${STATUS_COLORS[q.status]}`}>
                                {STATUS_LABELS[q.status]}
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground truncate">
                              {SERVICE_LABELS[q.service_type]} · {q.email} · {q.phone}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {new Date(q.created_at).toLocaleString("fr-BE")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                            <SelectTrigger className="w-36" data-testid={`quote-status-${q.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.keys(STATUS_LABELS).map((k) => (
                                <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => setSelectedQuote(q)}
                            className="p-2 rounded-sm border border-border hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
                            data-testid={`quote-view-${q.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteQuote(q.id)}
                            className="p-2 rounded-sm border border-border hover:border-destructive hover:text-destructive transition-colors"
                            data-testid={`quote-delete-${q.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-8">
            {contacts.length === 0 ? (
              <EmptyState text="Aucun message pour le moment." />
            ) : (
              <div className="grid gap-3">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    className={`border rounded-sm p-5 transition-colors ${
                      c.read ? "border-border bg-card/40" : "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5"
                    }`}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 shrink-0 rounded-full border border-border flex items-center justify-center">
                          <Mail className="w-4 h-4 text-[hsl(var(--accent))]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{c.name}</span>
                            {!c.read && (
                              <Badge className="text-[10px] bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30">
                                Nouveau
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                          <div className="text-sm text-foreground/90 mt-2 line-clamp-2">{c.message}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(c.created_at).toLocaleString("fr-BE")}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => {
                            setSelectedContact(c);
                            if (!c.read) markContactRead(c.id);
                          }}
                          className="p-2 rounded-sm border border-border hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
                          data-testid={`contact-view-${c.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!c.read && (
                          <button
                            onClick={() => markContactRead(c.id)}
                            className="p-2 rounded-sm border border-border hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                            data-testid={`contact-mark-read-${c.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteContact(c.id)}
                          className="p-2 rounded-sm border border-border hover:border-destructive hover:text-destructive transition-colors"
                          data-testid={`contact-delete-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Quote detail dialog */}
      <Dialog open={!!selectedQuote} onOpenChange={(o) => !o && setSelectedQuote(null)}>
        <DialogContent className="max-w-2xl bg-card border-border" data-testid="quote-detail-dialog">
          {selectedQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Demande de devis</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <DetailRow label="Nom" value={selectedQuote.full_name} />
                <DetailRow label="Société" value={selectedQuote.company || "—"} />
                <DetailRow label="Email" value={<a href={`mailto:${selectedQuote.email}`} className="text-[hsl(var(--accent))]">{selectedQuote.email}</a>} />
                <DetailRow label="Téléphone" value={<a href={`tel:${selectedQuote.phone}`} className="text-[hsl(var(--accent))]">{selectedQuote.phone}</a>} />
                <DetailRow label="Service" value={SERVICE_LABELS[selectedQuote.service_type]} />
                <DetailRow label="Surface" value={selectedQuote.surface || "—"} />
                <DetailRow label="Fréquence" value={selectedQuote.frequency || "—"} />
                <DetailRow label="Adresse" value={selectedQuote.address || "—"} />
                <DetailRow label="Message" value={selectedQuote.message || "—"} multi />
                <DetailRow label="Reçue le" value={new Date(selectedQuote.created_at).toLocaleString("fr-BE")} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact detail dialog */}
      <Dialog open={!!selectedContact} onOpenChange={(o) => !o && setSelectedContact(null)}>
        <DialogContent className="max-w-2xl bg-card border-border" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <DetailRow label="Nom" value={selectedContact.name} />
                <DetailRow label="Email" value={<a href={`mailto:${selectedContact.email}`} className="text-[hsl(var(--accent))]">{selectedContact.email}</a>} />
                <DetailRow label="Téléphone" value={selectedContact.phone || "—"} />
                <DetailRow label="Message" value={selectedContact.message} multi />
                <DetailRow label="Reçu le" value={new Date(selectedContact.created_at).toLocaleString("fr-BE")} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-border rounded-sm p-5 bg-card/40">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-9 h-9 rounded-full border flex items-center justify-center ${accent ? "border-[hsl(var(--accent))]" : "border-border"}`}>
          <Icon className={`w-4 h-4 ${accent ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`} />
        </div>
      </div>
      <div className="font-serif text-3xl mb-1">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-border rounded-sm p-16 text-center">
      <div className="text-muted-foreground">{text}</div>
    </div>
  );
}

function DetailRow({ label, value, multi }) {
  return (
    <div className={`grid ${multi ? "" : "grid-cols-3"} gap-4 py-3 border-b border-border last:border-b-0`}>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`${multi ? "mt-2 whitespace-pre-wrap" : "col-span-2"} text-foreground`}>{value}</div>
    </div>
  );
}
